import { create } from 'zustand';
import { PriceEditor } from '@/domain/pricing/services/price-editor';
import { SalesRepConfig } from '@domain/configuration/types';
import { transformOrderEntryItemToPriceEditorConfig } from '@/experiences/sales-portal/domain/ordering/screens/order-entry/blocs/transformers/order-entry-item-to-price-editor-config-transformer';
import type { SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment } from '@/types';

interface PricingStoreState {
  /** Map of variant pack UUID → PriceEditor instance */
  editors: Map<string, PriceEditor>;
}

interface PricingStoreActions {
  /** Clear all editors (call on restaurant/supplier change) */
  reset: () => void;

  /**
   * Sync editors with fresh data from server.
   * Updates existing editors (preserving dirty state) and creates new ones.
   *
   * @param items - Order entry items from the server
   * @param salesRepConfig - Current sales rep config for building PriceEditorConfigs
   */
  syncEditors: (
    items: SalesPortalOrderEntryTable_OrderEntryItemFieldsFragment[],
    salesRepConfig: SalesRepConfig,
  ) => void;
}

export const usePricingStore = create<PricingStoreState & PricingStoreActions>(
  (set, get) => ({
    editors: new Map(),

    reset: () => {
      set({ editors: new Map() });
    },

    syncEditors: (items, salesRepConfig) => {
      const { editors } = get();

      for (const item of items) {
        const vpUUID = item.variant_pack_id;
        const config = transformOrderEntryItemToPriceEditorConfig(
          item,
          salesRepConfig,
        );

        const existing = editors.get(vpUUID);
        if (existing) {
          existing.updateConfig(config);
        } else {
          editors.set(vpUUID, new PriceEditor(config));
        }
      }
    },
  }),
);
